import React, { useEffect, useMemo, useState } from "react";
import { Textarea, Input, Switch, Slider, Select, SelectItem, Spinner } from "@heroui/react";
import { AgentConfigInput } from "@webuild-ai/admin/models/components";
import { LLMModel, Tool } from "@webuild-ai/agent-platform/models/components";
import { useTranslation } from "react-i18next";

import { useWorkflowStore } from "@/stores/WorkflowStore";
import { useChatStore } from "@/stores/ChatStore";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { logger } from "@/telemetry";

import { ToolPicker } from "./ToolPicker";

interface WorkflowSettingsProps {
  isOpen: boolean;
}

export const WorkflowSettings: React.FC<WorkflowSettingsProps> = ({ isOpen }) => {
  const { workflow, setWorkflow, updateAgentTools } = useWorkflowStore();
  const { runId } = useChatStore();
  const { t } = useTranslation();

  const { platformClient } = useWebuildAPIClient();

  const [models, setModels] = useState<LLMModel[]>([]);
  const [available_tools, setAvailableTools] = useState<Tool[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!platformClient) {
      throw new Error("Platform client not found");
    }
    const fetchModels = async () => {
      try {
        const models = await platformClient.models.list();
        const tools = await platformClient.tools.list();

        setAvailableTools(tools);
        setModels(models);
      } catch (error) {
        logger.error("Failed to fetch models or tools", error);
      } finally {
        setIsLoading(false);
      }
    };

    setIsLoading(true);
    fetchModels();
  }, [platformClient, isOpen]);

  const updateAgentParameter = (agent_id: string, param: string, value: string) => {
    if (!workflow) return;

    const updatedWorkflow = {
      ...workflow,
      agents: {
        ...workflow.agents,
        [agent_id]: {
          ...workflow.agents[agent_id],
          parameters: {
            ...workflow.agents[agent_id].parameters,
            [param]: value,
          },
        },
      },
    };

    setWorkflow(updatedWorkflow);
  };

  const updateAgentParameters = (agent_id: string, paramsToUpdate: Record<string, any>) => {
    if (!workflow) return;

    const updatedWorkflow = {
      ...workflow,
      agents: {
        ...workflow.agents,
        [agent_id]: {
          ...workflow.agents[agent_id],
          parameters: {
            ...workflow.agents[agent_id].parameters,
            ...paramsToUpdate,
          },
        },
      },
    };

    setWorkflow(updatedWorkflow);
  };

  const renderParameters = (agent_id: string, agent: AgentConfigInput) => {
    const parameters = Object.entries(agent.parameters).map(([param, value], index) => {
      const label = param.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
      const uniqueKey = `${agent_id}-param-${param}-${index}`;

      if (param === "temperature") {
        const parsedValue = parseFloat(String(value));

        return (
          <Slider
            key={uniqueKey}
            className="max-w-md"
            data-id={`param-${param}-slider`}
            defaultValue={parsedValue}
            isDisabled={!!runId}
            label={label}
            maxValue={1}
            minValue={0}
            showSteps={true}
            step={0.1}
            onChange={(val) => updateAgentParameter(agent_id, param, String(val))}
          />
        );
      }

      if (param === "model") {
        const modelExists = models.some((m) => m.id === value);
        const isDisabled = !!runId || models.length === 0;
        const selectedKeys = value && modelExists ? [String(value)] : [];

        return (
          <div key={uniqueKey} className="relative">
            <Select
              className="z-50"
              data-id={`param-${param}-select`}
              isDisabled={isDisabled}
              items={models}
              label={label}
              placeholder={models.length === 0 ? "Loading models..." : "Select a model"}
              popoverProps={{
                classNames: {
                  content:
                    "z-50 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-lg min-w-[200px]",
                },
                shouldCloseOnBlur: false,
                shouldCloseOnInteractOutside: (element) => {
                  // Don't close if clicking on the Select component itself
                  const isSelectElement = element.closest(`[data-id="${param}-${param}-select"]`);
                  const isSelectButton =
                    element.hasAttribute("data-slot") &&
                    element.getAttribute("data-slot") === "trigger";
                  const isSelectIcon = element.closest('[data-slot="selectorIcon"]');

                  return !isSelectElement && !isSelectButton && !isSelectIcon;
                },
              }}
              selectedKeys={selectedKeys}
              size="sm"
              onSelectionChange={(keys) => {
                const keysArray = Array.from(keys);

                if (keysArray.length > 0) {
                  const modelId = keysArray[0];

                  if (modelId) {
                    const model = models.find((m) => m.id === modelId);

                    updateAgentParameters(agent_id, {
                      model: modelId,
                      max_tokens: model?.maxTokens || 200000,
                    });
                  }
                }
              }}
            >
              {(model) => (
                <SelectItem key={model.id} textValue={model.name}>
                  {model.name}
                </SelectItem>
              )}
            </Select>
          </div>
        );
      }

      if (param === "max_tokens") {
        const parsedValue = parseInt(String(value));
        const model = models.find((m) => m.id === agent.parameters.model);
        const maxValue = model ? model.maxTokens : 200000;

        return (
          <Slider
            key={uniqueKey}
            className="max-w-md"
            data-id={`param-${param}-slider`}
            isDisabled={!!runId}
            label={label}
            maxValue={maxValue}
            minValue={0}
            step={1000}
            value={parsedValue}
            onChange={(val) => updateAgentParameter(agent_id, param, String(val))}
          />
        );
      }

      if (
        typeof value === "boolean" ||
        (typeof value === "string" &&
          (value.toLowerCase() === "true" || value.toLowerCase() === "false"))
      ) {
        return (
          <Switch
            key={uniqueKey}
            aria-label={label}
            className="pt-2"
            data-id={`param-${param}-switch`}
            defaultSelected={String(value).toLowerCase() === "true"}
            id={`${agent_id}-${param}`}
            isDisabled={!!runId}
            size="sm"
            onValueChange={(checked) => updateAgentParameter(agent_id, param, String(checked))}
          >
            {label}
          </Switch>
        );
      }

      if (typeof value === "string") {
        if (value.length > 20) {
          return (
            <Textarea
              key={uniqueKey}
              className="pt-4"
              data-id={`param-${param}-textarea`}
              isDisabled={!!runId}
              label={label}
              size="sm"
              type="text"
              value={value}
              onChange={(e) => updateAgentParameter(agent_id, param, e.target.value)}
            />
          );
        }

        return (
          <div key={uniqueKey} data-id={`param-${param}-container`}>
            <span className="pt-2 text-small text-default-500">{label}</span>
            <Input
              className="pt-2"
              data-id={`param-${param}-input`}
              isDisabled={!!runId}
              size="sm"
              type="text"
              value={value}
              onChange={(e) => updateAgentParameter(agent_id, param, e.target.value)}
            />
          </div>
        );
      }

      if (typeof value === "number") {
        return (
          <Slider
            key={uniqueKey}
            className="max-w-md"
            data-id={`param-${param}-slider`}
            defaultValue={value}
            isDisabled={!!runId}
            label={label}
            maxValue={5000}
            minValue={0}
            step={1}
            onChange={(val) => updateAgentParameter(agent_id, param, String(val))}
          />
        );
      }

      return null;
    });

    return <React.Fragment key={`${agent_id}-parameters`}>{parameters}</React.Fragment>;
  };

  const renderAgentConfig = (agent_id: string, agent: AgentConfigInput) => {
    return (
      <div
        key={`${agent_id}-config`}
        className="gap-6 flex flex-col"
        data-id={`agent-${agent_id}-config`}
      >
        <ToolPicker
          agent={agent}
          agentId={agent_id}
          availableTools={available_tools}
          isDisabled={!!runId}
        />
        {renderParameters(agent_id, agent)}
      </div>
    );
  };

  // Resolve chat agent entry for User Memory toggle
  const chatAgentEntry = useMemo(() => {
    if (!workflow?.agents) return null;

    return (
      Object.entries(workflow.agents).find(([_, agent]) => agent.type === "chat_agent") ?? null
    );
  }, [workflow]);

  const isMemoryEnabled = useMemo(() => {
    if (!chatAgentEntry) return false;
    const tools: string[] = chatAgentEntry[1]?.parameters?.tools ?? [];

    return tools.includes("save_to_memory");
  }, [chatAgentEntry]);

  const handleMemoryToggle = (val: boolean) => {
    if (!chatAgentEntry) return;
    const [agentId] = chatAgentEntry;

    updateAgentTools(agentId, "save_to_memory", val);
    localStorage.setItem("userMemoryEnabled", JSON.stringify(val));
  };

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center p-4">
        <Spinner />
      </div>
    );
  }

  if (!workflow) {
    return null;
  }

  return (
    <div className="w-full flex flex-col pt-2" data-id="workflow-settings">
      {Object.entries(workflow.agents).map(([agent_id, agent]) => (
        <div key={agent_id} data-id={`agent-${agent_id}`}>
          <h3 className="text-md text-primary-700">
            {agent_id.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())}
          </h3>
          {renderAgentConfig(agent_id, agent)}
        </div>
      ))}

      {/* User Memory Section */}
      {chatAgentEntry && (
        <div className="flex flex-col gap-2 pt-4 pb-2" data-id="user-memory-section">
          <span className="text-small text-default-500">{t("Memory")}</span>
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-default-200 hover:bg-default-50 transition-background">
            <div className="w-7 h-7 flex items-center justify-center rounded-md bg-default-100 border border-default-200 shrink-0">
              <img alt="Memory" className="w-4 h-4 object-contain" src="/brands/mem0-icon.svg" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{t("User Memory")}</div>
              <div className="text-[11px] text-default-400">
                {t("Remember facts about you for personalised responses")}
              </div>
            </div>
            <Switch
              aria-label={t("Toggle User Memory")}
              color="success"
              data-id="user-memory-toggle"
              isDisabled={!!runId}
              isSelected={isMemoryEnabled}
              size="sm"
              onValueChange={handleMemoryToggle}
            />
          </div>
        </div>
      )}
    </div>
  );
};
